import React, { useState, Fragment } from "react";
import "./FeedbackCard.scss";
import { Menu, MenuItem } from "@mui/material";
import {
  CodeIcon,
  EditIcon,
  PauseIcon,
  PlayIcon,
  DuplicateIcon,
  ArrowForwardIcon,
  DeactivateIcon,
  TriggerScreenshotIcon,
  TriggerShakeIcon,
  TriggerWarningIcon,
  TriggerCustomIcon,
  CheckIcon,
  TriggerFloatingButtonIcon,
} from "../icons";
import {
  PulseButton,
  PulsePopupContainerSize,
} from "@pulselabs-ai/shared-fe-ui";
import CustomDialog from "../customDialog/CustomDialog";
import { PulsePopupConfirmation } from "../PulsePopupConfirmation/PulsePopupConfirmation";
// import { TrashcanIcon } from "../icons";

interface FeedbackCardProps {
  title: string;
  status: string;
  triggerType: string;
  questionsCount: number;
  rowCount: number | string;
  menuVisible?: boolean;
  onViewCode?: () => void;
  onEdit?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onDuplicate?: () => void;
  onDeactivate?: () => void;
  launchCustomAction?: () => void;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  style?: React.CSSProperties;
}

const FeedbackCard: React.FC<FeedbackCardProps> = ({
  title,
  status,
  triggerType,
  questionsCount,
  rowCount,
  menuVisible = true,
  onEdit,
  onPause,
  onResume,
  onDuplicate,
  onDeactivate,
  launchCustomAction,
  selectable = false,
  selected = false,
  onSelect,
  style,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDocsPopup, setShowDocsPopup] = useState(false);
  const [showSetupPopup, setShowSetupPopup] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewCode = () => {
    console.log("Opening docs popup");
    setShowDocsPopup(true);
  };

  const handleSetup = () => {
    console.log("Opening setup popup");
    setShowSetupPopup(true);
  };

  // const RESUME = { label: "Resume", icon: <PauseIcon />, onClick: onPause };

  const VIEW_CODE = {
    label: "View Code",
    icon: <CodeIcon />,
    onClick: handleViewCode,
  };
  const EDIT = {
    label: "Edit",
    icon: <EditIcon />,
    onClick: onEdit ?? (() => {}),
  };
  const PAUSE = {
    label: "Pause",
    icon: <PauseIcon />,
    onClick: onPause ?? (() => {}),
  };
  const DUPLICATE = {
    label: "Duplicate",
    icon: <DuplicateIcon />,
    onClick: onDuplicate ?? (() => {}),
  };
  // TODO: Delete is not available in the API yet
  // const DELETE = {
  //   label: "Delete",
  //   icon: <TrashcanIcon />,
  //   onClick: onDeactivate ?? (() => {}),
  // };
  const DEACTIVATE = {
    label: "Deactivate",
    icon: <DeactivateIcon />,
    onClick: () => setShowDeactivateConfirm(true),
  };
  const RESUME = {
    label: "Resume",
    icon: <PlayIcon />,
    onClick: onResume ?? (() => {}),
  };

  const getMenuItems = (status: string, triggerType: string) => {
    if (
      triggerType === "Custom" &&
      (status === "LAUNCHED" || status === "PAUSED")
    ) {
      status += "_CUSTOM";
    }
    console.log(triggerType, status);

    switch (status) {
      case "DRAFT":
        return [VIEW_CODE, EDIT, DUPLICATE, DEACTIVATE];
      case "LAUNCHED":
        // Same as InProgress
        return [EDIT, PAUSE];
      case "LAUNCHED_CUSTOM":
        // Same as InProgress
        return [VIEW_CODE, EDIT, PAUSE, DUPLICATE, DEACTIVATE];
      case "PAUSED":
        return [EDIT, RESUME];
      case "PAUSED_CUSTOM":
        return [VIEW_CODE, EDIT, RESUME, DUPLICATE, DEACTIVATE];
      case "CLOSED":
        return [DUPLICATE];
      default:
        return [];
    }
  };

  const menuItems = getMenuItems(status, triggerType);

  const getDisplayStatus = (status: string) => {
    switch (status) {
      case "LAUNCHED":
        return "IN PROGRESS";
      case "CLOSED":
        return "DEACTIVATED";
      default:
        return status;
    }
  };

  return (
    <Fragment>
      <div
        className={`feedback-card ${selected ? "selected" : ""}`}
        onClick={() => selectable && onSelect?.()}
        style={{ cursor: selectable ? "pointer" : "default", ...style }}
      >
        <div className="card-header">
          <span
            className={`status ${status === "CLOSED" ? "status-closed" : ""}`}
          >
            {getDisplayStatus(status)}
          </span>
          {menuVisible && (
            <button className="more-options" onClick={handleMenuOpen}>
              ⋮
            </button>
          )}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {menuItems.map((item, index) => (
              <MenuItem
                key={index}
                onClick={() => {
                  item.onClick();
                  handleMenuClose();
                }}
                className="menu-item"
              >
                {item.icon}
                <span>{item.label}</span>
              </MenuItem>
            ))}
          </Menu>
        </div>
        <div className="title-row">
          <h3>{title}</h3>
          <div className="count">
            {typeof rowCount === "string" && rowCount === "" ? (
              <span className="row-count"></span>
            ) : (
              <span className="row-count">{rowCount}</span>
            )}
          </div>
        </div>
        <div className="card-footer">
          <div className="trigger-type card-footer-item">
            <span className="icon">
              {triggerType === "Shake" && <TriggerShakeIcon />}
              {triggerType === "Screenshot" && <TriggerScreenshotIcon />}
              {triggerType === "FloatingButton" && (
                <TriggerFloatingButtonIcon />
              )}
              {triggerType === "Custom" && status === "DRAFT" && (
                <TriggerWarningIcon />
              )}
              {triggerType === "Custom" && status !== "DRAFT" && (
                <TriggerCustomIcon />
              )}
            </span>
            {triggerType === "Shake" && <span>SHAKE DEVICE</span>}
            {triggerType === "Screenshot" && <span>SCREEN GRAB</span>}
            {triggerType === "FloatingButton" && <span>FLOATING BUTTON</span>}
            {triggerType === "Custom" && status === "DRAFT" && (
              <span onClick={() => handleSetup()}>REQUIRES SETUP</span>
            )}
            {triggerType === "Custom" && status !== "DRAFT" && (
              <span>CUSTOM ACTION</span>
            )}
          </div>
          <ArrowForwardIcon className="arrow-icon" />
          <div className="questions-count card-footer-item">
            {questionsCount} QUESTIONS
          </div>
        </div>
      </div>
      <CustomDialog
        isOpen={showDocsPopup}
        onClose={() => {
          console.log("Closing docs popup");
          setShowDocsPopup(false);
        }}
        containerSize={PulsePopupContainerSize.SMALL}
        roundedCorners={true}
      >
        <div className="docs-dialog">
          <h2>Create Custom Triggers</h2>
          <div className="docs-links">
            <PulseButton
              onClick={() =>
                window.open(
                  "https://pulselabs.gitbook.io/pulse-flightrecorder-docs/sdk-documentation/pulse-labs-flightrecorder-documentation/ios/create-custom-triggers",
                  "_blank",
                )
              }
            >
              iOS Documentation
            </PulseButton>
            <PulseButton
              onClick={() =>
                window.open(
                  "https://pulselabs.gitbook.io/pulse-flightrecorder-docs/sdk-documentation/pulse-labs-flightrecorder-documentation/android/create-custom-triggers",
                  "_blank",
                )
              }
            >
              Android Documentation
            </PulseButton>
          </div>
        </div>
      </CustomDialog>
      <CustomDialog
        isOpen={showSetupPopup}
        onClose={() => {
          console.log("Closing setup popup");
          setShowSetupPopup(false);
        }}
      >
        <div className="setup-dialog">
          <h2>Configure Custom action</h2>
          <div className="docs-links">
            <PulseButton
              onClick={() =>
                window.open(
                  "https://pulselabs.gitbook.io/pulse-flightrecorder-docs/sdk-documentation/pulse-labs-flightrecorder-documentation/ios/create-custom-triggers",
                  "_blank",
                )
              }
            >
              iOS Documentation
            </PulseButton>
            <PulseButton
              onClick={() =>
                window.open(
                  "https://pulselabs.gitbook.io/pulse-flightrecorder-docs/sdk-documentation/pulse-labs-flightrecorder-documentation/android/create-custom-triggers",
                  "_blank",
                )
              }
            >
              Android Documentation
            </PulseButton>
          </div>
          <div className="configured-button">
            <PulseButton
              startIcon={<CheckIcon />}
              fullWidth
              onClick={() => {
                launchCustomAction?.();
                setShowSetupPopup(false);
              }}
            >
              THIS HAS BEEN CONFIGURED
            </PulseButton>
          </div>
        </div>
      </CustomDialog>
      <PulsePopupConfirmation
        isOpen={showDeactivateConfirm}
        onClose={() => setShowDeactivateConfirm(false)}
        icon={<DeactivateIcon />}
        title="Are you sure you want to deactivate this capture method?"
        description="Feedback will no longer be collected for this capture method, but you will be able to duplicate it to create a new capture method."
        confirmButtonText="DEACTIVATE"
        cancelButtonText="CANCEL"
        onConfirm={onDeactivate ?? (() => {})}
      />
    </Fragment>
  );
};

export default FeedbackCard;
